import {
  Category,
  CategoryDiagnosisType,
  DepartmentCategories,
  DepartmentOrderPreferences,
  DepartmentProducts,
  DocumentNeededOrders,
  ModernFormItem,
  ModernIcd10Description,
  PlanOfCareField,
  ProviderSigner,
  Subcategory,
} from "../../app/javascript/components/__generated__/graphql";
import { PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit";

import { RootState } from "./reduxStore";
import { getCategoryDiagnosisTypeIdToDiagnosisTypeMap } from "@verse_core/shared_components/orderCreation/util";

interface SelectedDepartmentState {
  departmentProductData: DepartmentProducts;
  departmentCategories: DepartmentCategories | null;
  signers: ProviderSigner[];
  documentNeededOrders: DocumentNeededOrders | null;
  departmentOrderPreferences: DepartmentOrderPreferences[];
  currentOrderPayor: string | null;
}

const getInitialState: () => SelectedDepartmentState = () => {
  return {
    departmentProductData: {
      compressionStandardItems: {
        armSleeveStandardItems: null,
        braStandardItems: null,
        stockingStandardItems: null,
        wrapStandardItems: null,
      },
      ostomyStandardItems: [],
      urologyStandardItems: [],
      woundCareStandardItems: [],
      cpapStandardItems: [],
      offTheShelfOrthoticsStandardItems: [],
      nebulizerStandardItems: [],
      ventilatorStandardItems: [],
      customOrthoticsStandardItems: [],
      prostheticsStandardItems: [],
      woundSuctionStandardItems: [],
      wheelchairsAndScootersStandardItems: [],
      diabetesStandardItems: [],
    },
    departmentCategories: null,
    signers: [],
    documentNeededOrders: null,
    departmentOrderPreferences: [],
    currentOrderPayor: null,
  };
};

const departmentSlice = createSlice({
  name: "department",
  initialState: getInitialState(),
  reducers: {
    setDepartmentProductData: (
      state,
      action: PayloadAction<DepartmentProducts>,
    ) => {
      state.departmentProductData = action.payload;
    },
    setDepartmentCategories: (
      state,
      action: PayloadAction<DepartmentCategories>,
    ) => {
      state.departmentCategories = action.payload;
    },
    setSigners: (state, action: PayloadAction<ProviderSigner[]>) => {
      state.signers = action.payload;
    },
    setDocumentNeededOrders: (
      state,
      action: PayloadAction<DocumentNeededOrders>,
    ) => {
      state.documentNeededOrders = action.payload;
    },
    setDepartmentOrderPreferences: (
      state,
      action: PayloadAction<DepartmentOrderPreferences[]>,
    ) => {
      state.departmentOrderPreferences = action.payload;
    },
    setCurrentOrderPayor: (state, action: PayloadAction<string | null>) => {
      state.currentOrderPayor = action.payload;
    },
    /** Clears a previous department's data before loading another one's order form. */
    resetDepartmentState: () => getInitialState(),
  },
});

export const {
  setDepartmentProductData,
  setDepartmentCategories,
  setSigners,
  setDocumentNeededOrders,
  setDepartmentOrderPreferences,
  setCurrentOrderPayor,
  resetDepartmentState,
} = departmentSlice.actions;

export const selectDepartmentCategories = (state: RootState) =>
  state.department.departmentCategories;

export const selectIsDepartmentProductDataLoaded = (state: RootState) => {
  return (
    state.department.departmentProductData.ostomyStandardItems.length > 0 ||
    state.department.departmentProductData.urologyStandardItems.length > 0 ||
    state.department.departmentProductData.woundCareStandardItems.length > 0 ||
    state.department.departmentProductData.compressionStandardItems
      .armSleeveStandardItems !== null ||
    state.department.departmentProductData.compressionStandardItems
      .braStandardItems !== null ||
    state.department.departmentProductData.compressionStandardItems
      .stockingStandardItems !== null ||
    state.department.departmentProductData.compressionStandardItems
      .wrapStandardItems !== null
  );
};

export const selectBasicDepartmentCategoryInfos = createSelector(
  [selectDepartmentCategories],
  (departmentCategories) =>
    departmentCategories?.categories.map((category) => ({
      id: category.id,
      identifier: category.identifier,
      displayName: category.displayName,
      isNew: category.isNew,
      isRetired: category.isRetired,
    })) ?? [],
);

export const selectCategoryFromId = (categoryId: number) =>
  createSelector(
    [selectDepartmentCategories],
    (departmentCategories): Category | null =>
      departmentCategories?.categories.find(
        (category) => category.id === categoryId,
      ) || null,
  );

export type VariantInfoType = {
  id: number;
  categoryId: number;
  subcategoryId: number;
  formItem: ModernFormItem;
  subcategoryName: string;
  optionGroupNameToSelectedOptionName: Record<string, string>;
  quantity?: number;
};

export const selectVariantIdToInfoMapForCategory = (categoryId: number) =>
  createSelector(
    [selectCategoryFromId(categoryId)],
    (category: Category | null) => {
      const map: Map<number, VariantInfoType> = new Map<
        number,
        VariantInfoType
      >();
      if (!category) {
        return map;
      }
      for (const subcategory of category.subcategories || []) {
        for (const formItem of subcategory.formItems || []) {
          for (const variant of formItem.variants || []) {
            map.set(variant.id, {
              id: variant.id,
              categoryId: category.id,
              subcategoryId: subcategory.id,
              subcategoryName: subcategory.name,
              formItem: formItem,
              optionGroupNameToSelectedOptionName: formItem.optionGroups.reduce(
                (acc, optionGroup) => {
                  const selectedOption = optionGroup.options.find((option) =>
                    variant.formItemOptionIds.includes(option.id),
                  );
                  if (selectedOption) {
                    acc[optionGroup.name] = selectedOption.name;
                  }
                  return acc;
                },
                {} as Record<string, string>,
              ),
              quantity: variant.quantity ?? undefined,
            });
          }
        }
      }
      return map;
    },
  );

export const selectSubcategoryIdToVariantIdsMap = createSelector(
  [selectDepartmentCategories],
  (departmentCategories) => {
    const map: Map<number, number[]> = new Map();
    departmentCategories?.categories.forEach((category) => {
      category.subcategories.forEach((subcategory) => {
        const variantIds = subcategory.formItems.flatMap((formItem) =>
          formItem.variants.map((variant) => variant.id),
        );
        map.set(subcategory.id, variantIds);
      });
    });

    return map;
  },
);

export const selectVariantIdsForSubcategoryId = (subcategoryId: number) =>
  createSelector(
    [selectSubcategoryIdToVariantIdsMap],
    (subcategoryIdToVariantIdsMap) =>
      subcategoryIdToVariantIdsMap.get(subcategoryId),
  );

export const selectSubcategoryFromName = (
  categoryId: number,
  subcategoryName: string,
) =>
  createSelector(
    [selectDepartmentCategories],
    (departmentCategories): Subcategory | null =>
      departmentCategories?.categories
        .find((category) => category.id === categoryId)
        ?.subcategories.find(
          (subcategory) => subcategory.name === subcategoryName,
        ) || null,
  );

export const selectCategoryDiagnosisTypes = (categoryId: number) =>
  createSelector(
    [selectCategoryFromId(categoryId)],
    (category: Category | null): CategoryDiagnosisType[] =>
      category?.categoryDiagnosisTypes || [],
  );

export const selectCategoryIdToCategoryDiagnosisTypeMap = createSelector(
  [selectDepartmentCategories],
  (
    departmentCategories: DepartmentCategories | null,
  ): Record<number, CategoryDiagnosisType[]> => {
    if (!departmentCategories) {
      return {};
    }
    return departmentCategories.categories.reduce(
      (acc, category) => {
        acc[category.id] = category.categoryDiagnosisTypes;
        return acc;
      },
      {} as Record<number, CategoryDiagnosisType[]>,
    );
  },
);

export const selectCategoryDiagnosisTypeIdToDiagnosisTypeMap = createSelector(
  [selectDepartmentCategories],
  getCategoryDiagnosisTypeIdToDiagnosisTypeMap,
);

export const selectPlanOfCareFieldsForCategory = (categoryId: number) =>
  createSelector(
    [selectDepartmentCategories],
    (departmentCategories: DepartmentCategories | null): PlanOfCareField[] => {
      if (!departmentCategories) {
        return [];
      }
      const matchedCategory = departmentCategories.categories.find(
        (category) => category.id === categoryId,
      );
      return matchedCategory?.planOfCareFields ?? [];
    },
  );

export const selectDefaultIcd10DiagnosisForCategory = (categoryId: number) =>
  createSelector(
    [selectDepartmentCategories],
    (
      departmentCategories: DepartmentCategories | null,
    ): ModernIcd10Description[] => {
      if (!departmentCategories) {
        return [];
      }
      const matchedCategory = departmentCategories.categories.find(
        (category) => category.id === categoryId,
      );
      return matchedCategory?.defaultIcd10Descriptions ?? [];
    },
  );

export const selectSigners = (state: RootState) => state.department.signers;

export const selectDocumentNeededOrders = (state: RootState) =>
  state.department.documentNeededOrders;

export const selectDepartmentOrderPreferences = (state: RootState) =>
  state.department.departmentOrderPreferences;

export const selectCurrentOrderPayor = (state: RootState) =>
  state.department.currentOrderPayor;

export default departmentSlice.reducer;
